import { ApiError } from "./api";
import { SERVER_PROBLEM } from "./save";
import type { SortColumn } from "./sort";

/** Roadmap 080 C3: the pages of the customer portal, and the path of each. */
export const PORTAL_TABS = ["home", "locations", "pickups", "rebates", "alerts", "support", "settings"] as const;
export type PortalTab = (typeof PORTAL_TABS)[number];
export const TAB_LABELS: Record<PortalTab, string> = {
  home: "Home",
  locations: "Locations",
  pickups: "Pickups",
  rebates: "Rebates",
  alerts: "Alerts",
  support: "Support",
  settings: "Settings",
};
export const NOT_OPEN = "This page is not open yet. Try again later.";
export const PAGE_GONE = "You cannot see this Location. Ask the owner of your organization.";
const NO_CONNECTION = "Cannot reach Xflow. Check your connection, then try again.";
export const HIGH_PCT = 75; // HIGH_SHARE in platform/api/app/portal_pages.py
export const PICKUP_DAYS = [90, 365, 730] as const;

export interface PortalLevel {
  pct: number | null;
  state: "full" | "not_full" | null;
  estimated: boolean;
  read_at: string | null;
  stale: boolean;
}

export interface PortalTank {
  name: string;
  kind: string;
  size_gal: number;
  monitored: boolean;
  level_pct: number | null;
  level_state: string | null;
  read_at: string | null;
  stale: boolean;
}

export interface PortalPlace {
  id: string;
  name: string;
  address: string;
  city: string;
  collector: string | null;
  since: string | null;
  level: PortalLevel | null;
  next_pickup: string | null;
  projected_pct: number | null;
  last_pickup: string | null;
  tanks: PortalTank[];
  /** 080 C4: the open pickup request; an older API leaves it out. */
  open_request?: PortalRequest | null;
  /** Each open request of any kind; an older API leaves it out. */
  open_requests?: PortalRequest[];
}

export type RequestStatus = "received" | "scheduled" | "collected" | "cancelled";

export type ServiceKind = "pickup" | "container" | "lock" | "other";
export type TimeWindow = "any" | "morning" | "afternoon";

export interface PortalRequest {
  location_id: string;
  location: string | null;
  collector: string | null;
  requested_on: string;
  need_by: string;
  status: RequestStatus;
  scheduled_on: string | null;
  from: "portal" | "collector";
  note: string;
  /** WF-23: an older API leaves these out; then the request is a pickup at any time. */
  kind?: ServiceKind;
  time_window?: TimeWindow;
  photo_id?: string | null;
}

export interface PortalRequests {
  days: number;
  more: boolean;
  requests: PortalRequest[];
  can_request: string[];
}

export interface RequestSent {
  created: boolean;
  request: PortalRequest;
  /** The web sets it: what the user sent with the request. */
  with?: { note: boolean; photo: boolean };
}

export interface PortalTotals {
  year: number;
  locations: number;
  monitored: number;
  high_at_next: number;
  collected_gal_year: number;
  open_requests?: number;
}

export interface PortalHome {
  as_of: string;
  totals?: PortalTotals;
  locations: PortalPlace[];
  /** 080 M5: an older API leaves it out. */
  contacts?: PortalContact[];
}

export interface PortalPickup {
  location_id: string;
  location: string | null;
  collector: string | null;
  on: string;
  gallons: number;
}

export interface PortalPickups {
  days: number;
  more: boolean;
  pickups: PortalPickup[];
}

export interface PortalLink {
  location_id: string;
  location: string | null;
  collector: string | null;
  since: string;
  until: string | null;
  active: boolean;
  share_history: boolean;
  data_use_consent: boolean;
}

export interface PortalOrganization {
  id: string;
  name: string;
  kind: string;
  parent_id: string | null;
  locations: number;
}

export interface PortalSettings {
  email: string | null;
  roles: string[];
  organizations: PortalOrganization[];
  links: PortalLink[];
  /** An older API leaves these out: no support line and no theft mail switch. */
  contacts?: PortalContact[];
  theft_mail?: boolean;
  gets_theft_mail?: boolean;
}

export const NO_CONTACT_HELP = "For a problem with this app or with sign-in, send a message to your collector on Support.";

/** SO-1430: the switch shows only for an owner or a manager, and only when the API sends it. */
export const showsTheftMail = (settings: PortalSettings) => settings.theft_mail !== undefined && settings.gets_theft_mail === true;

/** SO-1484: the line for app and sign-in problems names the collectors, never a platform address. */
export function supportLineText(contacts: readonly PortalContact[] | undefined): string {
  const shown = (contacts ?? []).filter((contact) => contact.phone || contact.email);
  if (shown.length === 0) return NO_CONTACT_HELP;
  return `For a problem with this app or with sign-in, contact your collector: ${shown.map((contact) => [contact.collector, contact.phone, contact.email].filter(Boolean).join(", ")).join("; ")}.`;
}

/** The tab of a portal path, or null for a path that is not a portal page. */
export function portalTab(path: string): PortalTab | null {
  const match = path.match(/^\/portal(?:\/([a-z]+))?\/?$/i);
  if (!match) return null;
  const tab = (match[1] ?? "home").toLowerCase();
  if (tab === "accept") return "home";
  return (PORTAL_TABS as readonly string[]).includes(tab) ? (tab as PortalTab) : null;
}

export const tabPath = (tab: PortalTab) => (tab === "home" ? "/portal" : `/portal/${tab}`);

/** FastAPI answers "Not Found" for a route it does not have: an older API, during a release. */
export function notOpen(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.detail === "Not Found";
}

/** The words for a page that did not load. The portal answers "not found" for no Location. */
export function pageError(error: unknown): string {
  if (notOpen(error)) return NOT_OPEN;
  if (error instanceof ApiError && error.status === 404) return PAGE_GONE;
  if (error instanceof ApiError && error.status === 401) return "Your session ended. Sign in again.";
  if (error instanceof ApiError) return SERVER_PROBLEM;
  return NO_CONNECTION;
}

const DAY = 86_400_000;

/** A date of the API (YYYY-MM-DD) as "Tue Oct 20", with the year when it is not this year. */
export function dayText(iso: string | null | undefined, today: Date = new Date()): string {
  if (!iso) return "–";
  const day = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(day.getTime())) return "–";
  const year = day.getFullYear() === today.getFullYear() ? undefined : "numeric";
  return day.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year });
}

/** "just now", "12 min ago", "9 h ago", "3 days ago". */
export function agoText(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "–";
  const ms = now.getTime() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "–";
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(ms / DAY)} days ago`;
}

/** The level of a Location in words: "52%", "~52%" for an estimate, "Full", or "No monitor". */
export function levelText(level: PortalLevel | null | undefined): string {
  if (!level) return "No level yet";
  if (level.pct !== null && level.pct !== undefined) return `${level.estimated ? "~" : ""}${level.pct}%`;
  if (level.state === "full") return "Full";
  if (level.state === "not_full") return "Not full";
  return "No level yet";
}

/** The width of the level bar, 0 to 100, or null when there is no percent. */
export function barPct(pct: number | null | undefined): number | null {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  return Math.min(100, Math.max(0, pct));
}

/** U-20: "Container 2". An older API names a container "Tank 2". */
export const containerName = (name: string) => name.replace(/^Tank (\d+)$/, "Container $1");

export const tankName = (tank: PortalTank) => `${containerName(tank.name)} · ${Math.round(tank.size_gal)} gal ${tank.kind}`;

export function tankText(tank: PortalTank): string {
  const name = tankName(tank);
  if (!tank.monitored) return `${name} · no monitor`;
  if (tank.level_pct !== null) return `${name} · ${tank.level_pct}%`;
  if (tank.level_state === "full") return `${name} · Full`;
  if (tank.level_state === "not_full") return `${name} · Not full`;
  return `${name} · no reading yet`;
}

export function nextPickupText(place: PortalPlace, today: Date = new Date()): string {
  if (!place.collector) return "No collector";
  return place.next_pickup ? `about ${dayText(place.next_pickup, today)}` : "Not planned yet";
}

export const gallonsText = (gal: number) => `${Math.round(gal).toLocaleString("en-US")} gal`;
export const countText = (n: number) => n.toLocaleString("en-US");

/** The Home tiles. An older API sends no totals: then the tiles count the rows. */
export function tiles(home: PortalHome): { value: string; label: string }[] {
  const places = home.locations;
  const totals = home.totals ?? {
    year: new Date().getFullYear(),
    locations: places.length,
    monitored: places.filter((p) => p.tanks.some((t) => t.read_at)).length,
    high_at_next: places.filter((p) => (p.projected_pct ?? 0) >= HIGH_PCT).length,
    collected_gal_year: Number.NaN,
  };
  const open = totals.open_requests ?? places.reduce((sum, p) => sum + openRequests(p).length, 0);
  return [
    { value: String(totals.locations), label: `locations (${totals.monitored} monitored)` },
    { value: String(open), label: open === 1 ? "request open" : "requests open" },
    { value: String(totals.high_at_next), label: `over ${HIGH_PCT}% at next pickup` },
    {
      value: Number.isFinite(totals.collected_gal_year) ? gallonsText(totals.collected_gal_year) : "–",
      label: `collected in ${totals.year}`,
    },
  ];
}

export function staleCount(places: PortalPlace[]): number {
  return places.filter((place) => place.level?.stale).length;
}

/** The collectors of the Locations, for the filter chips. */
export function collectors(places: PortalPlace[]): string[] {
  return [...new Set(places.map((place) => place.collector ?? "No collector"))].sort();
}

export interface PlaceFilter {
  text: string;
  collector: string | null;
  soon: boolean;
}

/** Search on the name, the address and the city; a collector chip; "pickup in the next 7 days". */
export function filterPlaces(places: PortalPlace[], filter: PlaceFilter, today: Date = new Date()): PortalPlace[] {
  const words = filter.text.trim().toLowerCase();
  const end = today.getTime() + 7 * DAY;
  return places.filter((place) => {
    if (filter.collector && (place.collector ?? "No collector") !== filter.collector) return false;
    if (words && !`${place.name} ${place.address} ${place.city}`.toLowerCase().includes(words)) return false;
    if (filter.soon) {
      if (!place.next_pickup) return false;
      if (new Date(`${place.next_pickup}T12:00:00`).getTime() > end) return false;
    }
    return true;
  });
}

export type PlaceSort = "name" | "collector" | "level" | "next" | "projected" | "last";

const SORT_VALUE: Record<PlaceSort, (place: PortalPlace) => string | number | null> = {
  name: (p) => p.name.toLowerCase(),
  collector: (p) => (p.collector ?? "").toLowerCase(),
  level: (p) => p.level?.pct ?? (p.level?.state === "full" ? 100 : null),
  next: (p) => p.next_pickup,
  projected: (p) => p.projected_pct,
  last: (p) => p.last_pickup,
};

/** Sort by one column; an empty value is always last. Ties keep the name order. */
export function sortPlaces(places: PortalPlace[], key: PlaceSort, up: boolean): PortalPlace[] {
  const value = SORT_VALUE[key];
  return [...places].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (x === null && y === null) return a.name.localeCompare(b.name);
    if (x === null) return 1;
    if (y === null) return -1;
    const order = x < y ? -1 : x > y ? 1 : 0;
    return (up ? order : -order) || a.name.localeCompare(b.name);
  });
}

export const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  manager: "Location manager",
  staff: "Location staff",
};

export const KIND_LABELS: Record<string, string> = {
  head_office: "Head office",
  region: "Region",
  franchisee: "Franchisee",
  location_group: "Location group",
};

export interface OrgNode {
  org: PortalOrganization;
  depth: number;
}

/** The organizations as a tree in reading order. An organization with no parent shown is a root. */
export function orgTree(orgs: PortalOrganization[]): OrgNode[] {
  const ids = new Set(orgs.map((org) => org.id));
  const children = new Map<string | null, PortalOrganization[]>();
  for (const org of orgs) {
    const parent = org.parent_id && ids.has(org.parent_id) ? org.parent_id : null;
    children.set(parent, [...(children.get(parent) ?? []), org]);
  }
  const out: OrgNode[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const org of [...(children.get(parent) ?? [])].sort((a, b) => a.name.localeCompare(b.name))) {
      if (seen.has(org.id)) continue;
      seen.add(org.id);
      out.push({ org, depth });
      walk(org.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

export function linkStatusText(link: PortalLink, today: Date = new Date()): string {
  if (link.active) return "Active";
  if (link.until) return `Ended ${dayText(link.until, today)}`;
  return `Starts ${dayText(link.since, today)}`;
}

/** The pickups of each Location, newest first, with the gallons of the shown rows. */
export function pickupTotals(rows: PortalPickup[]): { count: number; gallons: number } {
  return { count: rows.length, gallons: rows.reduce((sum, row) => sum + row.gallons, 0) };
}

// See NEED_DAYS, NEED_DAYS_MAX and NOTE_MAX in platform/api/app/pickup_requests.py.
export const NEED_DAYS = 7;
export const NEED_DAYS_MAX = 365;
export const NOTE_MAX = 500;
export const REQUEST_NOT_OPEN = "Requests are not open yet. Call your collector, or try again later.";
export const REQUEST_STATUS_TEXT: Record<RequestStatus, string> = {
  received: "Received",
  scheduled: "Scheduled",
  collected: "Collected",
  cancelled: "Cancelled",
};
export const OPEN_STATUSES: readonly RequestStatus[] = ["received", "scheduled"];

/** The day as YYYY-MM-DD in the time zone of the browser. */
export function isoDay(day: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

export function plusDays(iso: string, days: number): string {
  const day = new Date(`${iso}T12:00:00`);
  day.setDate(day.getDate() + days);
  return isoDay(day);
}

export const CLOSED_AT_VISIT = "Closed at the last visit";

/** "Received", "Scheduled for Tue Oct 20" or "Collected". A service is not collected: it closes at a visit. */
export function requestStatusText(request: Pick<PortalRequest, "status" | "scheduled_on" | "kind">, today: Date = new Date()): string {
  if (request.status === "collected" && request.kind && request.kind !== "pickup") return CLOSED_AT_VISIT;
  const text = REQUEST_STATUS_TEXT[request.status] ?? request.status;
  if (request.status === "scheduled" && request.scheduled_on) return `${text} for ${dayText(request.scheduled_on, today)}`;
  return text;
}

export const isOpen = (request: Pick<PortalRequest, "status"> | null | undefined) => !!request && OPEN_STATUSES.includes(request.status);

/** The open requests of a Location, of each kind. An older API sends only the open pickup request. */
export function openRequests(place: Pick<PortalPlace, "open_request" | "open_requests">): PortalRequest[] {
  const found = place.open_requests ?? (place.open_request ? [place.open_request] : []);
  return found.filter(isOpen);
}

/** A Location has at most one open pickup request (CP-8): then the row has no Request button. */
export const hasOpenPickup = (place: Pick<PortalPlace, "open_request" | "open_requests">) =>
  openRequests(place).some((request) => (request.kind ?? "pickup") === "pickup");

/** The chip of an open request on a row: "Lock · Received · needed by Fri, Oct 30". */
export function openRequestText(request: PortalRequest, today: Date = new Date()): string {
  return `${serviceText(request)} · ${requestStatusText(request, today)} · needed by ${dayText(request.need_by, today)}`;
}

export const SERVICE_KINDS: readonly ServiceKind[] = ["pickup", "container", "lock", "other"];
export const SERVICE_TEXT: Record<ServiceKind, string> = {
  pickup: "Pickup",
  container: "Container (new or change)",
  lock: "Lock",
  other: "Other service",
};
export const WINDOWS: readonly TimeWindow[] = ["any", "morning", "afternoon"];
export const WINDOW_TEXT: Record<TimeWindow, string> = { any: "Any time", morning: "Morning", afternoon: "Afternoon" };
export const NEED_NOTE = "Add a note: say what you need.";

export const serviceText = (request: Pick<PortalRequest, "kind">) => SERVICE_TEXT[request.kind ?? "pickup"] ?? SERVICE_TEXT.pickup;
export const windowText = (request: Pick<PortalRequest, "time_window">) => WINDOW_TEXT[request.time_window ?? "any"] ?? WINDOW_TEXT.any;

/** The request body. An older API ignores the WF-23 fields, so they go only when they are not the default. */
export function requestBody(
  locationId: string,
  needBy: string,
  note: string,
  kind: ServiceKind = "pickup",
  window: TimeWindow = "any",
  photoId: string | null = null,
): { location_id: string; need_by: string; note: string; kind?: ServiceKind; time_window?: TimeWindow; photo_id?: string } {
  return {
    location_id: locationId,
    need_by: needBy,
    note: note.trim(),
    ...(kind !== "pickup" ? { kind } : {}),
    ...(window !== "any" ? { time_window: window } : {}),
    ...(photoId ? { photo_id: photoId } : {}),
  };
}

export type RequestField = "need_by" | "note" | "photo";
export type RequestIssue = { field: RequestField | null; text: string };

/** After a change to the fields, the shown problem of those fields goes or changes; the other problems stay. */
export function recheckIssue(shown: RequestIssue | null, found: RequestIssue | null, fields: readonly RequestField[]): RequestIssue | null {
  if (!shown || !shown.field || !fields.includes(shown.field)) return shown;
  return found && found.field && fields.includes(found.field) ? found : null;
}

/** The first problem of the request form and its field, or null. The day is from today to one year ahead. */
export function requestFormIssue(needBy: string, note: string, today: string, kind: ServiceKind = "pickup"): RequestIssue | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(needBy)) return { field: "need_by", text: "Enter the day by which you need the pickup." };
  if (needBy < today) return { field: "need_by", text: "The day cannot be before today." };
  if (needBy > plusDays(today, NEED_DAYS_MAX)) return { field: "need_by", text: "The day is at most one year ahead." };
  if (note.trim().length > NOTE_MAX) return { field: "note", text: `The note can have ${NOTE_MAX} characters at most.` };
  if (kind !== "pickup" && !note.trim()) return { field: "note", text: NEED_NOTE };
  return null;
}

export const requestFormProblem = (needBy: string, note: string, today: string, kind: ServiceKind = "pickup") =>
  requestFormIssue(needBy, note, today, kind)?.text ?? null;

/** A message for a note over NOTE_MAX characters; the form keeps all the text, so nothing is cut silently. */
export function noteLengthText(note: string): string | null {
  const length = note.trim().length;
  return length > NOTE_MAX ? `The note is ${countText(length)} characters. The limit is ${countText(NOTE_MAX)}.` : null;
}

/** The sent requests of a page by Location id; they stay when the page loads its rows again. */
export type SentBy = Readonly<Record<string, RequestSent>>;

export const keepSent = (held: SentBy, id: string, sent: RequestSent): SentBy => ({ ...held, [id]: sent });

export function dropSent(held: SentBy, id: string): SentBy {
  if (!(id in held)) return held;
  const rest = { ...held };
  delete rest[id];
  return rest;
}

/** The state at the start of a load: a reload of the same page keeps its rows, so an open panel stays. */
export function loadStart<S extends { kind: string }>(prev: S, sameKey: boolean, loading: S): S {
  return sameKey && prev.kind === "ok" ? prev : loading;
}

/** The words after a send: a new request, or the open request that the collector has already. */
export function requestSentText(sent: RequestSent, today: Date = new Date()): string {
  const request = sent.request;
  const collector = request.collector ?? "your collector";
  if (!sent.created) {
    const status = requestStatusText(request, today);
    const open = `This Location has an open ${KIND_OPEN[request.kind ?? "pickup"] ?? "pickup"} request already (${status.charAt(0).toLowerCase()}${status.slice(1)}, needed by ${dayText(request.need_by, today)}). ${collector} has it.`;
    const lost = [sent.with?.note && "note", sent.with?.photo && "photo"].filter(Boolean).join(" and ");
    return lost ? `${open} Your ${lost} ${lost.includes(" and ") ? "were" : "was"} not sent. To add more, send a message on Support.` : open;
  }
  const what = request.kind && request.kind !== "pickup" ? `a request for ${KIND_SENT[request.kind]}` : "a pickup";
  const window = request.time_window && request.time_window !== "any" ? ` (${WINDOW_TEXT[request.time_window].toLowerCase()})` : "";
  const photo = sent.with?.photo ? ", with 1 photo" : "";
  return `Sent to ${collector}: ${what} by ${dayText(request.need_by, today)}${window}${photo}. You see its status on the row and on Pickups, under Requests.`;
}

const KIND_SENT: Record<ServiceKind, string> = { pickup: "a pickup", container: "a container", lock: "the lock", other: "a service" };
const KIND_OPEN: Record<ServiceKind, string> = { pickup: "pickup", container: "container", lock: "lock", other: "service" };

/** The photo upload found an open request of the kind (an API with the check); then the request goes without it. */
export const openAlready = (error: unknown) => error instanceof ApiError && error.status === 409 && error.detail === "open_request";

/** The words for a refused request. A 404 "Not Found" is an older API with no request route. */
export function requestFailText(error: unknown): string {
  if (notOpen(error)) return REQUEST_NOT_OPEN;
  if (error instanceof ApiError && error.status === 404) return PAGE_GONE;
  if (error instanceof ApiError && error.status === 401) return "Your session ended. Sign in again.";
  if (error instanceof ApiError && (error.status === 422 || error.status === 429) && error.detail) return error.detail;
  if (error instanceof ApiError) return SERVER_PROBLEM;
  return NO_CONNECTION;
}

/** The Locations on which the portal user can request a pickup. An older API sends no "can". */
export function canRequest(me: { locations: { id: string; can?: string[] }[] }): Set<string> {
  return new Set(me.locations.filter((place) => !place.can || place.can.includes("portal_pickup_request.create")).map((place) => place.id));
}

/** The level words of one tank, as the gauge shows them. */
export function tankLevelText(tank: PortalTank): string {
  if (tank.level_pct !== null) return `${tank.level_pct}%`;
  if (tank.level_state === "full") return "Full";
  if (tank.level_state === "not_full") return "Not full";
  return tank.monitored ? "No reading yet" : "No monitor";
}

/** The text of a tank gauge: "61% · next pickup about Tue Oct 20 · last reading 12 min ago". */
export function gaugeText(tank: PortalTank, place: Pick<PortalPlace, "collector" | "next_pickup">, now: Date = new Date()): string {
  const level = tankLevelText(tank);
  const next = !place.collector ? "no collector" : place.next_pickup ? `next pickup about ${dayText(place.next_pickup, now)}` : "next pickup not planned yet";
  const parts = [level, next];
  if (tank.read_at) parts.push(`last reading ${agoText(tank.read_at, now)}`);
  return parts.join(" · ");
}

/** 080 C5: one rebate line of a Location, with the dates of its period (WF-20). */
export interface PortalStatement {
  location_id: string;
  location: string | null;
  collector: string | null;
  period_start: string;
  period_end: string;
  first_pickup: string | null;
  last_pickup: string | null;
  pickups: number;
  gallons: number;
  amount: number;
  status: "paid" | "pending";
  paid_on: string | null;
}

export interface PortalPeriod {
  collector: string | null;
  period_start: string;
  period_end: string;
  locations: number;
  pickups: number;
  gallons: number;
  amount: number;
  paid: number;
}

export interface PortalRebates {
  days: number;
  more: boolean;
  statements: PortalStatement[];
  periods: PortalPeriod[];
}

export interface PortalAlert {
  id: string;
  location_id: string;
  location: string | null;
  collector: string | null;
  kind: string;
  at: string;
  tank: string | null;
  drop_gal: number | null;
  collector_status: "open" | "closed";
  acknowledged_at: string | null;
  can_acknowledge: boolean;
}

export interface PortalAlerts {
  days: number;
  more: boolean;
  alerts: PortalAlert[];
}

export interface AlertAcknowledged {
  created: boolean;
  alert: PortalAlert;
}

export const REBATE_DAYS = [365, 730, 1095] as const;
export const REBATES_NOT_OPEN = "Rebates are not open yet. Ask your collector, or try again later.";
export const ALERTS_NOT_OPEN = "Alerts are not open yet. Your collector calls you about a theft alert.";
export const REBATES_OWNER_ONLY = "Only the owner and the location managers of your organization see rebates and statements.";
export const ACK_OWNER_ONLY = "Only the owner or a location manager can acknowledge an alert.";
export const ACK_GONE = "You cannot acknowledge this alert now. Reload the page.";

/** The Locations on which the portal user sees rebates (OQ-B4). An older API sends no "can". */
export function canSeeRebates(me: { locations: { id: string; can?: string[] }[] }): Set<string> {
  return new Set(me.locations.filter((place) => !place.can || place.can.includes("portal_rebate.view")).map((place) => place.id));
}

/** A rebate in US dollars: "$1,234.50". */
export const moneyText = (amount: number) => amount.toLocaleString("en-US", { style: "currency", currency: "USD" });

const dateText = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/** The dates of a period, never a period code: "Jul 1, 2026 to Sep 30, 2026". */
export function periodText(start: string, end: string): string {
  return `${dateText(start)} to ${dateText(end)}`;
}

/** "Paid on Oct 1, 2026" or "Not paid yet". */
export function statementStatusText(line: Pick<PortalStatement, "status" | "paid_on">): string {
  if (line.status === "paid") return line.paid_on ? `Paid on ${dateText(line.paid_on)}` : "Paid";
  return "Not paid yet";
}

/** The Pickups cell of a statement line: "3 · Jul 21, 2026 to Sep 15, 2026". */
export function statementPickupsText(line: Pick<PortalStatement, "pickups" | "first_pickup" | "last_pickup">): string {
  const range = pickupRangeText(line);
  return range === "–" ? String(line.pickups) : `${line.pickups} · ${range}`;
}

/** The pickup dates of a line: "Sep 3, 2026 to Sep 24, 2026", or one date. */
export function pickupRangeText(line: Pick<PortalStatement, "first_pickup" | "last_pickup">): string {
  const first = line.first_pickup ?? line.last_pickup;
  const last = line.last_pickup ?? line.first_pickup;
  if (!first || !last) return "–";
  return first === last ? dateText(first) : periodText(first, last);
}

/** The page state with the words of the page for an older API that has no route for it. */
export function notOpenAs<T extends { kind: string; text?: string }>(state: T, words: string): T {
  return state.kind === "error" && state.text === NOT_OPEN ? { ...state, text: words } : state;
}

/** The words under the Rebates filters: why a rebate shows fewer gallons than Pickups (P-3, Q-26). */
export const NET_GALLONS_TEXT =
  "A rebate pays on the net gallons: the total gallons of each pickup less its scrap, the part that is not good oil. Pickups shows the total gallons.";

/** "Possible theft: the level fell 42 gal" (the kind of alert, and the drop when it is known). */
export function alertText(alert: Pick<PortalAlert, "kind" | "drop_gal">): string {
  const kind = alert.kind === "theft" ? "Possible theft" : "Container alert";
  return alert.drop_gal === null ? kind : `${kind}: the level fell ${gallonsText(alert.drop_gal)}`;
}

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** A time as "Tue, Oct 20, 2:14 AM" in the zone of the Location; a zone that is not the browser zone gets its name. */
export function whenText(iso: string, today: Date = new Date(), zone?: string | null): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "–";
  const year = at.getFullYear() === today.getFullYear() ? undefined : "numeric";
  const options: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric", year, hour: "numeric", minute: "2-digit" };
  if (!zone) return at.toLocaleString("en-US", options);
  try {
    return at.toLocaleString("en-US", { ...options, timeZone: zone, ...(zone === browserZone() ? {} : { timeZoneName: "short" }) });
  } catch {
    return at.toLocaleString("en-US", options);
  }
}

type Zoned = { locations: { id: string; timezone?: string | null }[] };

/** The time zone of each Location; an older API sends none. */
export function zones(me: Zoned): Map<string, string> {
  return new Map(me.locations.filter((place) => place.timezone).map((place) => [place.id, place.timezone as string]));
}

/** The one zone of all the Locations, or null when they have more than one zone or none. */
export function sharedZone(me: Zoned): string | null {
  const found = new Set(zones(me).values());
  return found.size === 1 ? [...found][0] : null;
}

export const collectorStatusText = (alert: Pick<PortalAlert, "collector_status">) =>
  alert.collector_status === "closed" ? "Closed by your collector" : "Open at your collector";

/** "Acknowledged 3 h ago" or "Not acknowledged". */
export function ackText(alert: Pick<PortalAlert, "acknowledged_at">, now: Date = new Date()): string {
  return alert.acknowledged_at ? `Acknowledged ${agoText(alert.acknowledged_at, now)}` : "Not acknowledged";
}

/** The words for a refused acknowledge. A 404 "Not Found" is an older API with no alert route. */
export function ackFailText(error: unknown): string {
  if (notOpen(error)) return ALERTS_NOT_OPEN;
  if (error instanceof ApiError && error.status === 403) return error.detail || ACK_OWNER_ONLY;
  if (error instanceof ApiError && error.status === 404) return ACK_GONE;
  return pageError(error);
}

/** The alerts with one alert changed, after an acknowledge. */
export function replaceAlert(alerts: PortalAlert[], changed: PortalAlert): PortalAlert[] {
  return alerts.map((alert) => (alert.id === changed.id ? changed : alert));
}

/** The tabs of the portal user. Rebates shows only with portal_rebate.view on a Location (CP-4). */
export function shownTabs(me: { locations: { id: string; can?: string[] }[] }): PortalTab[] {
  const rebates = canSeeRebates(me).size > 0;
  return PORTAL_TABS.filter((tab) => tab !== "rebates" || rebates);
}

/** "At next pickup": "~82%", or the day that the tank gets full when the forecast is over full. */
export function atNextText(place: Pick<PortalPlace, "projected_pct" | "next_pickup" | "level">, today: Date = new Date()): { text: string; full: boolean } {
  const projected = place.projected_pct;
  if (projected === null || projected === undefined || !Number.isFinite(projected)) return { text: "–", full: false };
  if (projected < 100) return { text: `~${projected}%`, full: false };
  const visit = place.next_pickup;
  if (!visit) return { text: "Full before the next pickup", full: true };
  const now = place.level?.pct ?? (place.level?.state === "full" ? 100 : null);
  const start = isoDay(today);
  const days = Math.round((new Date(`${visit}T12:00:00`).getTime() - new Date(`${start}T12:00:00`).getTime()) / DAY);
  if (now !== null && now < 100 && days > 0 && projected > now) {
    const full = plusDays(start, Math.floor((days * (100 - now)) / (projected - now)));
    if (full < visit) return { text: `Full by about ${dayText(full, today)}`, full: true };
  }
  return { text: `Full before ${dayText(visit, today)}`, full: true };
}

/** The newest reading of the Locations: "Levels as of Tue, Oct 20, 9:00 PM", in the zone of the Locations. */
export function asOfText(places: PortalPlace[], today: Date = new Date(), zone?: string | null): string {
  const times = places.flatMap((place) => [place.level?.read_at, ...place.tanks.map((tank) => tank.read_at)]);
  const newest = times.filter((at): at is string => !!at && !Number.isNaN(new Date(at).getTime())).sort((a, b) => new Date(a).getTime() - new Date(b).getTime()).pop();
  return newest ? `Levels as of ${whenText(newest, today, zone)}` : "No level reading yet";
}

export interface HomeView {
  sort: PlaceSort;
  up: boolean;
  soon: boolean;
}

/** The sort and the 7-day filter of Home from the query string, so they survive a reload. */
export function readHomeView(search: string): HomeView {
  const query = new URLSearchParams(search);
  const sort = query.get("sort") ?? "";
  return {
    sort: sort in SORT_VALUE ? (sort as PlaceSort) : "name",
    up: query.get("dir") !== "desc",
    soon: query.get("soon") === "1",
  };
}

export function homeSearch(view: HomeView): string {
  const query = new URLSearchParams();
  if (view.sort !== "name") query.set("sort", view.sort);
  if (!view.up) query.set("dir", "desc");
  if (view.soon) query.set("soon", "1");
  const text = query.toString();
  return text ? `?${text}` : "";
}

/** The Location and the period of a list page from the query string. */
export function readListView(search: string, ids: string[], periods: readonly number[], fallback: number): { where: string; days: number } {
  const query = new URLSearchParams(search);
  const where = query.get("location") ?? "";
  const days = Number(query.get("days"));
  return { where: ids.includes(where) ? where : "", days: periods.includes(days) ? days : fallback };
}

export function listSearch(where: string, days: number, fallback: number): string {
  const query = new URLSearchParams();
  if (where) query.set("location", where);
  if (days !== fallback) query.set("days", String(days));
  const text = query.toString();
  return text ? `?${text}` : "";
}

/** The words for a Home filter that shows no Location. */
export function noMatchText(filter: PlaceFilter): string {
  return filter.soon && !filter.text.trim() && !filter.collector ? "No pickup is planned in the next 7 days." : "No Location matches the filters.";
}

/** 080 M5: the contact of a collector for its customers; an older API sends none. */
export interface PortalContact {
  collector: string;
  phone: string;
  email: string;
}

/** The words of the Contact panel. With no phone and no email, they name the ways in the portal. */
export function contactPanelText(place: Pick<PortalPlace, "name" | "collector" | "since">, hasContact: boolean, today: Date = new Date()): string {
  if (!place.collector) return `${place.name} has no collector now.`;
  const since = place.since ? ` The portal link started on ${dayText(place.since, today)}.` : "";
  const how = hasContact
    ? `For a pickup, a spill or a container, contact ${place.collector}:`
    : "For a pickup or a service, use Request. For other news, send a message.";
  return `${place.name} is served by ${place.collector}.${since} ${how}`;
}

export function contactOf(contacts: PortalContact[] | undefined, collector: string | null): PortalContact | null {
  if (!collector) return null;
  return (contacts ?? []).find((contact) => contact.collector === collector && (contact.phone || contact.email)) ?? null;
}

export const telHref = (phone: string) => `tel:${phone.replace(/[^0-9+]/g, "")}`;

/** SO-1462: a message of a portal user about one Location to its collector. */
export interface PortalMessage {
  id: string;
  location_id: string;
  location: string | null;
  collector: string | null;
  sent_at: string;
  text: string;
  status: "open" | "done";
}

export interface PortalMessages {
  days: number;
  more: boolean;
  messages: PortalMessage[];
  can_send: string[];
  contacts: PortalContact[];
  text_max: number;
  /** True when the API has no message route yet: the contacts then come from Home. */
  lagging?: boolean;
}

export const MESSAGE_MAX = 1000; // BODY_MAX in platform/api/app/portal_messages.py
export const MESSAGES_NOT_OPEN = "The message form is not open yet. Use the phone or the email above.";
export const MESSAGE_STATUS: Record<PortalMessage["status"], string> = { open: "Sent", done: "Done by the collector" };

/** A message for a text over the limit; the form keeps all the text, so nothing is cut silently. */
export function messageLengthText(text: string, max: number = MESSAGE_MAX): string | null {
  const length = text.trim().length;
  return length > max ? `The message is ${countText(length)} characters. The limit is ${countText(max)}.` : null;
}

/** The first problem of the message form, or null. */
export function messageProblem(location: string, text: string, max: number = MESSAGE_MAX): string | null {
  if (!location) return "Choose the Location.";
  if (!text.trim()) return "Write a message.";
  if (text.trim().length > max) return `The message can have ${countText(max)} characters at most.`;
  return null;
}

/** The words for a message that did not go. A 422 or a 429 has words from the API. */
export function messageFailText(error: unknown): string {
  if (notOpen(error)) return MESSAGES_NOT_OPEN;
  if (error instanceof ApiError && (error.status === 422 || error.status === 429) && error.detail) return error.detail;
  if (error instanceof ApiError && error.status === 404) return "You cannot send a message about this Location now. Reload the page.";
  return pageError(error);
}

export const messageSentText = (message: PortalMessage) =>
  `Sent to ${message.collector ?? "your collector"}. The collector sees it with the Location ${message.location ?? ""}`.trim() + ".";

/** The query string of a list page, with the sort parameters of the current URL kept. */
export function withSorts(search: string, current: string, params: readonly string[]): string {
  const query = new URLSearchParams(search);
  const kept = new URLSearchParams(current);
  for (const param of params) {
    const value = kept.get(param);
    if (value) query.set(param, value);
  }
  const text = query.toString();
  return text ? `?${text}` : "";
}

const highest = (place: PortalPlace) => {
  const levels = place.tanks.map((tank) => tank.level_pct ?? (tank.level_state === "full" ? 100 : null)).filter((pct): pct is number => pct !== null);
  return levels.length ? Math.max(...levels) : null;
};

/** The sortable columns of the portal tables. */
export const LOCATION_COLUMNS: SortColumn<PortalPlace>[] = [
  { key: "location", label: "Location", value: (p) => p.name },
  { key: "collector", label: "Collector", value: (p) => p.collector },
  { key: "since", label: "Linked since", value: (p) => p.since, first: "desc" },
  { key: "tanks", label: "Containers", value: highest, first: "desc" },
];

export const PICKUP_COLUMNS: SortColumn<PortalPickup>[] = [
  { key: "date", label: "Date", value: (p) => p.on, first: "desc" },
  { key: "location", label: "Location", value: (p) => p.location },
  { key: "collector", label: "Collector", value: (p) => p.collector },
  { key: "gallons", label: "Gallons (total)", value: (p) => p.gallons, first: "desc", num: true },
];

export const PERIOD_COLUMNS: SortColumn<PortalPeriod>[] = [
  { key: "period", label: "Period", value: (p) => p.period_end, first: "desc" },
  { key: "collector", label: "Collector", value: (p) => p.collector },
  { key: "locations", label: "Locations", value: (p) => p.locations, first: "desc", num: true },
  { key: "pickups", label: "Pickups", value: (p) => p.pickups, first: "desc", num: true },
  { key: "gallons", label: "Gallons (net)", value: (p) => p.gallons, first: "desc", num: true },
  { key: "rebate", label: "Rebate", value: (p) => p.amount, first: "desc", num: true },
  { key: "paid", label: "Paid", value: (p) => p.paid, first: "desc", num: true },
];

export const STATEMENT_COLUMNS: SortColumn<PortalStatement>[] = [
  { key: "period", label: "Period", value: (s) => s.period_end, first: "desc" },
  { key: "location", label: "Location", value: (s) => s.location },
  { key: "collector", label: "Collector", value: (s) => s.collector },
  { key: "pickups", label: "Pickups", value: (s) => s.pickups, first: "desc" },
  { key: "gallons", label: "Gallons (net)", value: (s) => s.gallons, first: "desc", num: true },
  { key: "rebate", label: "Rebate", value: (s) => s.amount, first: "desc", num: true },
  { key: "status", label: "Status", value: (s) => s.status, order: ["pending", "paid"] },
];

export const ALERT_COLUMNS: SortColumn<PortalAlert>[] = [
  { key: "when", label: "When", value: (a) => a.at, first: "desc" },
  { key: "location", label: "Location", value: (a) => a.location },
  { key: "alert", label: "Alert", value: (a) => a.drop_gal, first: "desc" },
  { key: "collector", label: "Collector", value: (a) => a.collector },
  { key: "you", label: "You", value: (a) => (a.acknowledged_at ? "acknowledged" : "waiting"), order: ["waiting", "acknowledged"] },
];
